import {
  Component,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import {
  GcButtonComponent,
  GcPillComponent,
  GcTextComponent,
  IpoTranslatePipe,
} from '@ip-only/front-end-library';
import { pairwise, Subject, takeUntil } from 'rxjs';
import { SPAN_SELECTION_HELP_CLICKED } from '../../../shared/posthug-analytics/analytics-event-types';
import { AnalyticsService } from '../../../shared/posthug-analytics/analytics.service';
import { RolloutProgressReportEntities } from '../../../shared/self-inspection-reports/service/entities';
import { InspectionReportInitService } from '../../../shared/self-inspection-reports/service/initial-values/inspection-report-init.service';
import { RolloutProgressReportsService } from '../../../shared/self-inspection-reports/service/reports/rollout-progress-reports.service';
import { ResourcesService } from '../../../user/resources.service';
import { SelectSpanModeService } from '../../service/map/span/select-span-mode/select-span-mode.service';
import { HelpModalComponent } from './help-modal/help-modal.component';

@Component({
  selector: 'app-select-span-mode',
  imports: [
    GcTextComponent,
    GcButtonComponent,
    IpoTranslatePipe,
    HelpModalComponent,
    GcPillComponent,
  ],
  templateUrl: './select-span-mode.component.html',
  styleUrl: './select-span-mode.component.scss',
})
export class SelectSpanModeComponent implements OnInit, OnDestroy {
  private readonly selectSpanModeService = inject(SelectSpanModeService);
  private readonly reportService = inject(RolloutProgressReportsService);
  private readonly inspectionReportInitService = inject(
    InspectionReportInitService,
  );
  private readonly analyticsService = inject(AnalyticsService);
  private readonly resourceService = inject(ResourcesService);

  selectedSpanLength = this.selectSpanModeService.selectedSpanLength;
  isEditMode = this.selectSpanModeService.isEditMode;
  selectedSpanIds = this.selectSpanModeService.selectedSpanIds;

  requestBody: RolloutProgressReportEntities.v3.ReportRequestWithSignal = {
    ifsNumber: this.inspectionReportInitService.getInitValueProperty('ifs'),
    type: RolloutProgressReportEntities.v3.MarkerTypes.SPAN,
    spanIds: this.selectedSpanIds,
  };

  createV3ReportMutation = this.reportService.createV3Report$(this.requestBody);
  updateV3ReportMutation = this.reportService.updateV3ReportSpans$(
    this.selectedSpanIds,
  );

  userHasPreviouslyUsedMultiSpan = signal(
    localStorage.getItem('userHasPreviouslyUsedMultiSpan'),
  );
  isMultiSpanHelpModalOpen =
    this.selectSpanModeService.isMultiSpanHelpModalOpen;

  displayWarningMaxSpanLength =
    this.selectSpanModeService.displayWarningMaxSpanLength;

  cancelSub$ = new Subject<void>();

  constructor() {
    effect(() => {
      if (!this.userHasPreviouslyUsedMultiSpan()) {
        this.openHelpModal();
        localStorage.setItem('userHasPreviouslyUsedMultiSpan', 'true');
      }
    });
  }

  ngOnInit(): void {
    this.getSelectedResourceAndDestroyWhenIfsChanges();
  }

  ngOnDestroy(): void {
    this.cancelSub$.next();
    this.cancelSub$.complete();
  }

  getSelectedResourceAndDestroyWhenIfsChanges = () => {
    this.resourceService
      .getSelectedResource$()
      .pipe(takeUntil(this.cancelSub$), pairwise())
      .subscribe((res) => res.length > 1 && this.closeSelectSpanMode());
  };

  cancelSelectSpanMode() {
    //Insert error prevention logic here
    this.closeSelectSpanMode();
  }

  createReport() {
    this.createV3ReportMutation.mutate();
  }

  updateReport = () => {
    this.updateV3ReportMutation.mutate();
  };

  closeSelectSpanMode() {
    this.selectSpanModeService.setSelectSpanMode(false);
  }

  openHelpModal() {
    this.selectSpanModeService.setIsMultiSpanHelpModalOpen(true);
    this.analyticsService.logEvent(SPAN_SELECTION_HELP_CLICKED);
  }
}
